import React, { useState, useEffect } from 'react';
import { 
  X, 
  Type, 
  Save,
  Loader2,
  Tag,
  AlignLeft,
  Code
} from 'lucide-react';
import styles from './LeaveTypeDrawer.module.css';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import CustomSelect from '../../../components/Common/Select/CustomSelect';

const LeaveTypeDrawer = ({ isOpen, onClose, onSave, leaveType = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    short_name: '',
    status: 1
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const statusOptions = [
    { value: 1, label: 'Active' },
    { value: 0, label: 'Inactive' }
  ];

  const fetchLeaveType = async (id) => {
    setFetching(true);
    try {
      const response = await api.get(`/leave-type/${id}`);
      const data = response.data;
      setFormData({
        name: data.name || '',
        short_name: data.short_name || '',
        status: data.status === 0 ? 0 : 1
      });
    } catch (error) {
      console.error('Error fetching leave type details:', error);
      toast.error('Failed to load leave type details');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    if (leaveType && isOpen) {
      fetchLeaveType(leaveType.id);
    } else if (!leaveType) {
      setFormData({
        name: '',
        short_name: '',
        status: 1
      });
    }
  }, [isOpen, leaveType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData(prev => ({ 
      ...prev, 
      status: selectedOption ? selectedOption.value : '' 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;

    const payload = {
      ...formData,
      ...(leaveType ? { updated_by: userId } : { created_by: userId, updated_by: userId })
    };

    try {
      if (leaveType) {
        await api.put(`/leave-type/${leaveType.id}`, payload);
        toast.success('Leave type updated successfully');
      } else {
        await api.post('/leave-type', payload);
        toast.success('Leave type added successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving leave type:', error);
      toast.error(error.response?.data?.message || 'Failed to save leave type');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{leaveType ? 'Edit Leave Type' : 'Add New Leave Type'}</h2>
            <p>Define how employees can request time off.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {fetching ? (
              <div className={styles['fetching-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Loading details...</span>
              </div>
            ) : (
              <>
                {/* Leave Type Name */}
                <div className={styles['form-group']}>
                  <label><Type size={16} /> Leave Type Name <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Annual Leave"
                    required
                    className={styles['form-input']}
                  />
                </div>

                {/* Leave Type Code */}
                <div className={styles['form-group']}>
                  <label><Code size={16} /> Leave Type Code <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="short_name"
                    value={formData.short_name}
                    onChange={handleChange}
                    placeholder="e.g. AL"
                    required
                    className={styles['form-input']}
                  />
                </div>

                {/* Status */}
                <div className={styles['form-group']}>
                  <label><Tag size={16} /> Status <span className={styles['required-star']}>*</span></label>
                  <CustomSelect 
                    name="status"
                    value={statusOptions.find(t => t.value === formData.status)}
                    onChange={handleSelectChange}
                    options={statusOptions}
                    placeholder="Select Status"
                    required
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading || fetching}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{leaveType ? 'Update Leave Type' : 'Save Leave Type'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveTypeDrawer;
